// src/context/HybridDataProvider.tsx
// TypeScript version with full type definitions

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { createClient, SupabaseClient } from '@supabase/supabase-js'

// Supabase setup (optional - works without it!)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || ''
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || ''
const supabase: SupabaseClient | null = supabaseUrl && supabaseAnonKey 
  ? createClient(supabaseUrl, supabaseAnonKey) 
  : null

// Type Definitions
interface Project {
  id: string;
  name: string;
  address: string;
  status: string;
  progress: number;
  budget: number;
  spentToDate: number;
  phase: string;
  phaseProgress: number;
  startDate: Date;
  completionDate: Date;
  squareFootage: number;
  client?: string;
  is_demo?: boolean;
  is_local?: boolean;
  created_at?: string;
}

interface Vendor {
  id: string;
  name: string;
  bid: number;
  rating: number;
  reviews: number;
  certified: boolean;
  responseTime: string;
  trade: string;
  is_demo?: boolean;
}

interface Phase {
  id: string;
  name: string;
  progress: number;
  budget: number;
  spent: number;
  status: string;
  is_demo?: boolean;
}

interface SiteAnalysis {
  score: number;
  terrain?: {
    averageSlope: number;
    buildableArea: number;
  };
}

interface Preferences {
  hiddenDemoIds: string[];
  showDemoData: boolean;
  hasSeenWelcome: boolean;
}

interface HybridDataContextType {
  // Data
  projects: Project[];
  vendors: Vendor[];
  phases: Phase[];
  currentProject: Project | null;
  activeProject: Project | null;
  siteAnalysis: SiteAnalysis;
  inspections: any[];
  issues: any[];

  // User data
  user: any | null;
  profile: any | null;
  team: any | null;

  // State
  loading: boolean;
  error: string | null;
  isConnected: boolean;

  // Supabase
  supabase: SupabaseClient | null;

  // Functions
  deleteDemoItem: (itemId: string, itemType: 'project' | 'vendor' | 'phase') => void;
  clearAllDemoData: () => void;
  restoreDemoData: () => void;
  createProject: (projectData: Partial<Project>) => Promise<Project>;
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<Project>;
  refreshData: () => Promise<void>;

  // Preferences
  preferences: Preferences;
  dataMode: 'hybrid' | 'demo' | 'real';
  setDataMode: (mode: 'hybrid' | 'demo' | 'real') => void;

  // Helpers
  hasDemoData: boolean;
  hasRealData: boolean;
  demoCount: number;
  realCount: number;
}

// Default mock data
const DEFAULT_MOCK_DATA = {
  projects: [
    {
      id: '1',
      name: 'Maple Street Residence',
      address: '123 Maple St, Kennesaw, GA',
      status: 'active',
      progress: 67,
      budget: 485000,
      spentToDate: 142500,
      phase: 'Framing',
      phaseProgress: 77,
      startDate: new Date('2024-01-15'),
      completionDate: new Date('2024-07-30'),
      squareFootage: 4850,
      is_demo: true
    },
    {
      id: '2',
      name: 'Cedar Avenue Build',
      address: '456 Cedar Ave, Atlanta, GA',
      status: 'planning',
      progress: 23,
      budget: 350000,
      spentToDate: 45000,
      phase: 'Foundation',
      phaseProgress: 45,
      startDate: new Date('2024-03-01'),
      completionDate: new Date('2024-10-15'),
      squareFootage: 3200,
      is_demo: true
    },
    {
      id: '3',
      name: 'Downtown Project',
      address: '789 Main St, Marietta, GA',
      status: 'completed',
      progress: 100,
      budget: 750000,
      spentToDate: 725000,
      phase: 'Completed',
      phaseProgress: 100,
      startDate: new Date('2023-06-01'),
      completionDate: new Date('2024-02-28'),
      squareFootage: 6500,
      is_demo: true
    }
  ] as Project[],
  
  vendors: [
    { 
      id: '1', 
      name: 'Crest Electrical', 
      bid: 79500, 
      rating: 4.8, 
      reviews: 127, 
      certified: true, 
      responseTime: '2 hours',
      trade: 'electrical',
      is_demo: true
    },
    { 
      id: '2', 
      name: 'Perez Concrete', 
      bid: 83700, 
      rating: 4.5, 
      reviews: 89, 
      certified: true, 
      responseTime: '4 hours',
      trade: 'concrete',
      is_demo: true
    },
    { 
      id: '3', 
      name: 'Day Tile', 
      bid: 85600, 
      rating: 4.2, 
      reviews: 45, 
      certified: false, 
      responseTime: '1 day',
      trade: 'tile',
      is_demo: true
    }
  ] as Vendor[],

  phases: [
    { id: '1', name: 'Foundation', progress: 100, budget: 36500, spent: 36500, status: 'completed', is_demo: true },
    { id: '2', name: 'Framing', progress: 77, budget: 48000, spent: 37000, status: 'active', is_demo: true },
    { id: '3', name: 'MEP', progress: 0, budget: 52000, spent: 0, status: 'pending', is_demo: true },
    { id: '4', name: 'Interior', progress: 0, budget: 38000, spent: 0, status: 'pending', is_demo: true },
    { id: '5', name: 'Finish', progress: 0, budget: 28000, spent: 0, status: 'pending', is_demo: true }
  ] as Phase[],

  inspections: [],
  issues: [],
  
  siteAnalysis: {
    score: 85,
    terrain: {
      averageSlope: 3.2,
      buildableArea: 92
    }
  } as SiteAnalysis
}

// Context
const DataContext = createContext<HybridDataContextType | undefined>(undefined)

export const useHybridData = (): HybridDataContextType => {
  const context = useContext(DataContext)
  if (!context) throw new Error('useHybridData must be used within HybridDataProvider')
  return context
}

interface HybridDataProviderProps {
  children: ReactNode;
}

export const HybridDataProvider: React.FC<HybridDataProviderProps> = ({ children }) => {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  // Load preferences
  const getStoredPreferences = (): Preferences => {
    const stored = localStorage.getItem('homequest_preferences')
    return stored ? JSON.parse(stored) : {
      hiddenDemoIds: [],
      showDemoData: true,
      hasSeenWelcome: false
    }
  }

  const [preferences, setPreferences] = useState<Preferences>(getStoredPreferences)
  const [dataMode, setDataMode] = useState<'hybrid' | 'demo' | 'real'>(() => {
    return supabase ? 'hybrid' : 'demo'
  })

  // State type definition
  interface StateType {
    projects: Project[];
    vendors: Vendor[];
    phases: Phase[];
    currentProject: Project | null;
    activeProject: Project | null;
    siteAnalysis: SiteAnalysis;
    inspections: any[];
    issues: any[];
    user: any | null;
    profile: any | null;
    team: any | null;
    loading: boolean;
    error: string | null;
    isConnected: boolean;
  }

  // Initialize state with filtered mock data
  const initializeState = (): StateType => {
    const prefs = getStoredPreferences()

    // Filter out deleted demo items
    const filterDeleted = <T extends { id: string }>(items: T[]): T[] => {
      if (!prefs.showDemoData) return []
      return items.filter(item =>
        !prefs.hiddenDemoIds.includes(item.id)
      )
    }

    // Load persisted user/profile/project data from localStorage
    const loadFromStorage = (key: string) => {
      try {
        const stored = localStorage.getItem(`homequest_${key}`)
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    }

    // If we have Supabase, start with loading state and don't use ANY cached data
    // This ensures fresh data (including user/profile) is fetched from the database
    if (supabase) {
      return {
        projects: [],
        vendors: [],
        phases: [],
        currentProject: null,
        activeProject: null,
        siteAnalysis: DEFAULT_MOCK_DATA.siteAnalysis,
        inspections: [],
        issues: [],
        user: null,  // Don't load from cache - let useAuth handle it
        profile: null,  // Don't load from cache - let useAuth handle it
        team: null,  // Don't load from cache - fresh load from DB
        loading: true,
        error: null,
        isConnected: true
      }
    }

    // If no Supabase (offline mode), use cached data
    return {
      projects: filterDeleted(DEFAULT_MOCK_DATA.projects),
      vendors: filterDeleted(DEFAULT_MOCK_DATA.vendors),
      phases: filterDeleted(DEFAULT_MOCK_DATA.phases),
      currentProject: loadFromStorage('currentProject') || filterDeleted(DEFAULT_MOCK_DATA.projects)[0] || null,
      activeProject: loadFromStorage('activeProject') || filterDeleted(DEFAULT_MOCK_DATA.projects)[0] || null,
      siteAnalysis: DEFAULT_MOCK_DATA.siteAnalysis,
      inspections: [],
      issues: [],
      user: loadFromStorage('user'),
      profile: loadFromStorage('profile'),
      team: loadFromStorage('team'),
      loading: false,
      error: null,
      isConnected: false
    }
  }

  const [state, setState] = useState<StateType>(initializeState())

  // Save preferences
  useEffect(() => {
    localStorage.setItem('homequest_preferences', JSON.stringify(preferences))
  }, [preferences])

  // Don't persist user/profile/team to localStorage - they should be loaded fresh from Supabase each time
  // Only persist project selections for convenience
  useEffect(() => {
    if (state.currentProject) {
      localStorage.setItem('homequest_currentProject', JSON.stringify(state.currentProject))
    }
    if (state.activeProject) {
      localStorage.setItem('homequest_activeProject', JSON.stringify(state.activeProject))
    }
  }, [state.currentProject, state.activeProject])

  // Load real data (if available)
  useEffect(() => {
    if (supabase && dataMode !== 'demo') {
      loadRealData()
    }
  }, [dataMode])

  // Also load real data on initial mount if Supabase is available
  useEffect(() => {
    if (supabase && dataMode !== 'demo') {
      loadRealData()
    }
  }, [])

  const loadRealData = async (forceRefresh: boolean = false) => {
    if (!supabase) {
      console.error('❌ No Supabase client available!')
      return
    }

    try {
      setState(prev => ({ ...prev, loading: true }))
      console.log('🔍 Checking Supabase connection...', forceRefresh ? '(Force Refresh)' : '')

      const { data: { session }, error: sessionError } = await supabase.auth.getSession()

      if (sessionError) {
        console.error('❌ Session error:', sessionError)
        return
      }

      console.log('✅ Session found:', session?.user?.email)
      
      if (session) {
        // First get the user's profile to get their team_id
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('id', session.user.id)
          .single()

        // Then fetch only projects for their team
        const { data: realProjects, error: projectsError } = await supabase
          .from('projects')
          .select('*')
          .eq('team_id', userProfile?.team_id)
          .order('created_at', { ascending: false })

        if (projectsError) {
          console.error('Error loading projects from Supabase:', projectsError)
          console.error('Project error details:', {
            message: projectsError.message,
            code: projectsError.code,
            details: projectsError.details,
            hint: projectsError.hint
          })
        } else {
          console.log('Successfully loaded', realProjects?.length || 0, 'projects from Supabase')
        }

        const { data: realVendors, error: vendorsError } = await supabase
          .from('vendor_profiles')
          .select('*')

        if (vendorsError) {
          console.error('Error loading vendors:', vendorsError)
        }

        if (dataMode === 'hybrid') {
          const demoProjects = DEFAULT_MOCK_DATA.projects
            .filter(p => !preferences.hiddenDemoIds.includes(p.id))
            .map(p => ({ ...p, name: `[DEMO] ${p.name}` }))

          setState(prev => ({
            ...prev,
            projects: [...(realProjects || []), ...demoProjects],
            vendors: [...(realVendors || []), ...DEFAULT_MOCK_DATA.vendors],
            user: session.user,
            loading: false
          }))
        } else {
          setState(prev => ({
            ...prev,
            projects: realProjects || [],
            vendors: realVendors || [],
            user: session.user,
            loading: false
          }))
        }
      } else {
        setState(prev => ({ ...prev, loading: false }))
      }
    } catch (error: any) {
      console.error('Error loading real data:', error)
      setState(prev => ({ ...prev, loading: false, error: error.message }))
    }
  }

  // Delete demo item
  const deleteDemoItem = (itemId: string, itemType: 'project' | 'vendor' | 'phase') => {
    setPreferences(prev => ({
      ...prev,
      hiddenDemoIds: [...prev.hiddenDemoIds, itemId]
    }))

    setState(prev => {
      const updates = { ...prev }
      
      switch(itemType) {
        case 'project':
          updates.projects = prev.projects.filter(p => p.id !== itemId)
          if (prev.activeProject?.id === itemId) {
            updates.activeProject = prev.projects.find(p => p.id !== itemId) || null
          }
          if (prev.currentProject?.id === itemId) {
            updates.currentProject = prev.projects.find(p => p.id !== itemId) || null
          }
          break
        case 'vendor':
          updates.vendors = prev.vendors.filter(v => v.id !== itemId)
          break
        case 'phase':
          updates.phases = prev.phases.filter(p => p.id !== itemId)
          break
      }
      
      return updates
    })
  }

  // Clear all demo data
  const clearAllDemoData = () => {
    if (window.confirm('Remove all demo data? This cannot be undone.')) {
      setPreferences(prev => ({
        ...prev,
        showDemoData: false,
        hiddenDemoIds: []
      }))

      setState(prev => ({
        ...prev,
        projects: prev.projects.filter(p => !p.is_demo),
        vendors: prev.vendors.filter(v => !v.is_demo),
        phases: prev.phases.filter(p => !p.is_demo)
      }))
    }
  }

  // Restore demo data
  const restoreDemoData = () => {
    setPreferences(prev => ({
      ...prev,
      showDemoData: true,
      hiddenDemoIds: []
    }))

    setState(prev => ({
      ...prev,
      projects: [...prev.projects.filter(p => !p.is_demo), ...DEFAULT_MOCK_DATA.projects],
      vendors: [...prev.vendors.filter(v => !v.is_demo), ...DEFAULT_MOCK_DATA.vendors],
      phases: [...prev.phases.filter(p => !p.is_demo), ...DEFAULT_MOCK_DATA.phases]
    }))
  }

  // Create project
  const createProject = async (projectData: Partial<Project>): Promise<Project> => {
    if (supabase && state.user) {
      try {
        // Get user's team_id from profile
        const { data: userProfile } = await supabase
          .from('profiles')
          .select('team_id')
          .eq('id', state.user.id)
          .single()

        // Add team_id and user_id to project data
        const projectWithTeam = {
          ...projectData,
          team_id: userProfile?.team_id,
          user_id: state.user.id
        }

        const { data, error } = await supabase
          .from('projects')
          .insert(projectWithTeam)
          .select()
          .single()

        if (!error && data) {
          setState(prev => ({
            ...prev,
            projects: [data, ...prev.projects]
          }))
          return data
        }
      } catch (error) {
        console.error('Database save failed:', error)
      }
    }

    const newProject: Project = {
      id: `local_${Date.now()}`,
      name: '',
      address: '',
      status: 'planning',
      progress: 0,
      budget: 0,
      spentToDate: 0,
      phase: 'Planning',
      phaseProgress: 0,
      startDate: new Date(),
      completionDate: new Date(),
      squareFootage: 0,
      ...projectData,
      is_local: true,
      created_at: new Date().toISOString()
    }

    setState(prev => ({
      ...prev,
      projects: [newProject, ...prev.projects]
    }))

    return newProject
  }

  // Update project
  const updateProject = async (projectId: string, updates: Partial<Project>): Promise<Project> => {
    let foundProject: Project | undefined;
    
    if (supabase && state.user) {
      try {
        const { data, error } = await supabase
          .from('projects')
          .update(updates)
          .eq('id', projectId)
          .select()
          .single()

        if (!error && data) {
          setState(prev => ({
            ...prev,
            projects: prev.projects.map(p => 
              p.id === projectId ? { ...p, ...data } : p
            ),
            currentProject: prev.currentProject?.id === projectId 
              ? { ...prev.currentProject, ...data }
              : prev.currentProject,
            activeProject: prev.activeProject?.id === projectId 
              ? { ...prev.activeProject, ...data }
              : prev.activeProject
          }))
          return data
        }
      } catch (error) {
        console.error('Update failed:', error)
      }
    }

    // Local update
    foundProject = state.projects.find(p => p.id === projectId)
    if (!foundProject) {
      throw new Error(`Project with id ${projectId} not found`)
    }

    const updatedProject = { ...foundProject, ...updates }
    
    setState(prev => ({
      ...prev,
      projects: prev.projects.map(p => 
        p.id === projectId ? updatedProject : p
      ),
      currentProject: prev.currentProject?.id === projectId 
        ? updatedProject
        : prev.currentProject,
      activeProject: prev.activeProject?.id === projectId 
        ? updatedProject
        : prev.activeProject
    }))

    return updatedProject
  }

  // Refresh data function
  const refreshData = async () => {
    console.log('🔄 Refreshing data...')
    // Clear ALL cached localStorage data to force fresh load
    localStorage.removeItem('homequest_currentProject')
    localStorage.removeItem('homequest_activeProject')
    localStorage.removeItem('homequest_user')
    localStorage.removeItem('homequest_profile')
    localStorage.removeItem('homequest_team')

    // Reset state to force fresh load
    setState(prev => ({
      ...prev,
      projects: [],
      currentProject: null,
      activeProject: null,
      loading: true
    }))

    // Force reload from Supabase
    if (supabase && dataMode !== 'demo') {
      await loadRealData(true)
    }
  }

  // Provide value
  const value: HybridDataContextType = {
    ...state,
    supabase,
    deleteDemoItem,
    clearAllDemoData,
    restoreDemoData,
    createProject,
    updateProject,
    refreshData,
    preferences,
    dataMode,
    setDataMode,
    hasDemoData: state.projects.some(p => p.is_demo),
    hasRealData: state.projects.some(p => !p.is_demo),
    demoCount: state.projects.filter(p => p.is_demo).length,
    realCount: state.projects.filter(p => !p.is_demo).length
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}

export default HybridDataProvider